import { localStorageManager } from "@chakra-ui/color-mode";
import { ChakraProvider, ColorModeScript, extendTheme } from "@chakra-ui/react";
import { GoogleOAuthProvider } from "@react-oauth/google";
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";
import { QueryClient, QueryClientProvider } from "react-query";
import { Provider as ReduxProvider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { AuthProvider } from "./contexts/Auth";
import { persistor, store } from "./core/createStore";
import AppNavigator from "./navigation/AppNavigator";
import themeOverride from "./redwing-theme";
import { initPostHog } from "./shared/posthog";
import { captureRedditClickId } from "./shared/reddit";

// 1. Override colorMode to be dark theme by default
const alwaysDarkMode = {
  initialColorMode: "dark",
  useSystemColorMode: false,
};
const customColorModeManager = {
  ...localStorageManager,
  ...alwaysDarkMode,
};

// 2. Call `extendTheme` and pass your custom values
const theme = extendTheme(themeOverride);

// React Query client
const queryClient = new QueryClient();

// Sentry error reporting
Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  integrations: [new BrowserTracing()],
  tracesSampleRate: 1.0,
});

const googleLoginClientId: string =
  process.env.REACT_APP_GOOGLE_LOGIN_CLIENT_ID || "";

// Remember the Reddit ad click id (if any) so it's available at signup/checkout
captureRedditClickId();

// Product analytics (PostHog). Page views + identify are wired in the router.
initPostHog();

export const App = () => (
  <>
    <ReduxProvider store={store}>
      <PersistGate loading={<p>&nbsp;</p>} persistor={persistor}>
        <ColorModeScript initialColorMode="dark" />
        <ChakraProvider theme={theme} colorModeManager={customColorModeManager}>
          <GoogleOAuthProvider clientId={googleLoginClientId}>
            <AuthProvider>
              <QueryClientProvider client={queryClient}>
                <AppNavigator />
              </QueryClientProvider>
            </AuthProvider>
          </GoogleOAuthProvider>
        </ChakraProvider>
      </PersistGate>
    </ReduxProvider>
  </>
);
